import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  type NotificationPreferences,
  type NotificationPreferencesPatch,
} from '@tabbio/types';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '../lib/notifications-api';
import { QUERY_KEYS } from '../lib/query-keys';

export function useNotificationPreferences(enabled = true) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.NOTIFICATION_PREFERENCES,
    queryFn: fetchNotificationPreferences,
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  const mutation = useMutation<
    NotificationPreferences,
    Error,
    NotificationPreferencesPatch,
    { previous?: NotificationPreferences }
  >({
    mutationFn: updateNotificationPreferences,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.NOTIFICATION_PREFERENCES,
      });

      const previous = queryClient.getQueryData<NotificationPreferences>(
        QUERY_KEYS.NOTIFICATION_PREFERENCES,
      );
      const base = previous ?? DEFAULT_NOTIFICATION_PREFERENCES;
      queryClient.setQueryData<NotificationPreferences>(
        QUERY_KEYS.NOTIFICATION_PREFERENCES,
        { ...base, ...patch },
      );

      return { previous };
    },
    onError: (_error, _patch, context) => {
      queryClient.setQueryData(
        QUERY_KEYS.NOTIFICATION_PREFERENCES,
        context?.previous ?? DEFAULT_NOTIFICATION_PREFERENCES,
      );
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(
        QUERY_KEYS.NOTIFICATION_PREFERENCES,
        preferences,
      );
    },
  });

  return {
    preferences: query.data ?? DEFAULT_NOTIFICATION_PREFERENCES,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isSaving: mutation.isPending,
    error: query.error,
    refetch: query.refetch,
    updatePreferences: mutation.mutateAsync,
  };
}
